import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { ActionModal, FIELD_LABEL_CLASS, INPUT_CLASS } from "./ActionModal";
import { fmtActor, fmtDate } from "../../format";
import type {
  TrackingSettingsPayload,
  UpdateTrackingSettingsBody,
} from "../../types";

/**
 * Tracking Health's fleet-wide settings (backend ADR 0039) — the numbers an
 * operator may move without a deploy, in two tabs that match the backend's
 * own grouping:
 *
 * - **General** — the low-volume allowance: how few qualifying bookings, over
 *   how many days, before a Healthy hotel reads Low volume and Rules 1, 4 and
 *   5 stand down for it.
 * - **Advanced** — the click floor (Rule 6: too few Google Ads clicks marks
 *   the hotel Unhealthy) and the allowance's backstop (a zero that persists
 *   too long with clicks to match fails Rule 1 despite the allowance).
 *
 * The fields are written as the sentences the settings complete, with the
 * numbers inline, because that is how an operator reasons about them
 * ("fewer than 3 in each of the last two 14-day windows") — a bare
 * `lowVolumeWindowDays` label makes them translate.
 *
 * What the modal sends is ONLY the fields that changed, plus the reason. The
 * server refuses a body that changes nothing, so the submit is disabled until
 * something does; it also refuses an unknown key, an out-of-bounds value and
 * a backstop shorter than twice the window, so the same bounds are checked
 * here first (red text under the field, `ProofRequirementModal`'s pattern)
 * and the server's own sentence still comes back as the toast if the two
 * ever disagree.
 *
 * Nothing moves on save — the derived bucket and Rule 6 are re-read at the
 * next daily pass — and the modal says so beneath the reason, because an
 * operator who saves and refreshes the fleet table would otherwise read the
 * unchanged section as a failed save.
 */

/** The five whole-number settings; `clickFloorEnabled` is the sixth. */
type NumericKey =
  | "lowVolumeBookings"
  | "lowVolumeWindowDays"
  | "lowVolumeZeroBackstopDays"
  | "clickFloorClicks"
  | "clickFloorWindowDays";

/**
 * Bounds, mirrored EXACTLY from the backend's `settings/tracking-settings.ts`
 * — the server is the authority and refuses on its own; these exist so the
 * refusal reads under the field before the round trip.
 *
 * They must not be tightened. `lowVolumeBookings` bottoms out at 0 because 0
 * is the bucket's documented off switch (nothing holds fewer than zero, so no
 * hotel is ever classified and the Rule 1/4/5 allowance stands down with it).
 * A stricter `min: 1` here made that switch unreachable from the only write
 * surface there is — and, because `valid` spans every field rather than the
 * open tab, a stored row ALREADY holding 0 opened with an error on this tab
 * and Save disabled for all six settings until someone typed the off state
 * away. The cross-field rule (backstop ≥ 2 × window) is checked below.
 */
const BOUNDS: Record<NumericKey, { min: number; max: number }> = {
  lowVolumeBookings: { min: 0, max: 100 },
  lowVolumeWindowDays: { min: 7, max: 90 },
  lowVolumeZeroBackstopDays: { min: 14, max: 365 },
  clickFloorClicks: { min: 1, max: 10_000 },
  clickFloorWindowDays: { min: 1, max: 90 },
};

const NUMERIC_KEYS: NumericKey[] = [
  "lowVolumeBookings",
  "lowVolumeWindowDays",
  "lowVolumeZeroBackstopDays",
  "clickFloorClicks",
  "clickFloorWindowDays",
];

const GENERAL_KEYS: NumericKey[] = ["lowVolumeBookings", "lowVolumeWindowDays"];
const ADVANCED_KEYS: NumericKey[] = [
  "clickFloorClicks",
  "clickFloorWindowDays",
  "lowVolumeZeroBackstopDays",
];

type Tab = "general" | "advanced";

const TABS: Array<{ key: Tab; label: string }> = [
  { key: "general", label: "General" },
  { key: "advanced", label: "Advanced" },
];

/** `"12"` → 12; blank, fractional or non-numeric → null. The server coerces
 *  a numeric string the same way, so this is the one parse both sides do. */
function parseWhole(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const value = Number(trimmed);
  return Number.isInteger(value) ? value : null;
}

/** The text state a settings row seeds — one place, so mount and re-seed
 *  cannot drift. */
function textOf(payload: TrackingSettingsPayload): Record<NumericKey, string> {
  return {
    lowVolumeBookings: String(payload.lowVolumeBookings),
    lowVolumeWindowDays: String(payload.lowVolumeWindowDays),
    lowVolumeZeroBackstopDays: String(payload.lowVolumeZeroBackstopDays),
    clickFloorClicks: String(payload.clickFloorClicks),
    clickFloorWindowDays: String(payload.clickFloorWindowDays),
  };
}

export function SettingsModal({
  payload,
  onSubmit,
  onClose,
  pending,
}: {
  payload: TrackingSettingsPayload;
  onSubmit: (body: UpdateTrackingSettingsBody) => void;
  onClose: () => void;
  pending?: boolean;
}) {
  const [tab, setTab] = useState<Tab>("general");
  // Text, not numbers: a field being retyped passes through blank and
  // half-typed states that a number cannot represent without snapping.
  const [text, setText] = useState<Record<NumericKey, string>>(() =>
    textOf(payload),
  );
  const [clickFloorEnabled, setClickFloorEnabled] = useState(
    payload.clickFloorEnabled,
  );
  const [reason, setReason] = useState("");

  // The modal can MOUNT against a cached row: React Query serves the previous
  // read instantly on a reopen and the fresher row lands a moment later. Since
  // `payload` keeps driving the diff (:body), the "was" hints and the footer,
  // inputs seeded once at mount would quietly propose writing stale numbers
  // back over someone else's save. So re-seed when the row moves — but only
  // while the operator has typed nothing, because a refetch landing mid-edit
  // must never overwrite what they are in the middle of writing.
  const seeded = useRef({
    version: payload.version,
    text: textOf(payload),
    clickFloorEnabled: payload.clickFloorEnabled,
  });
  const untouched =
    reason === "" &&
    clickFloorEnabled === seeded.current.clickFloorEnabled &&
    NUMERIC_KEYS.every((key) => text[key] === seeded.current.text[key]);
  useEffect(() => {
    if (payload.version === seeded.current.version) return;
    if (!untouched) return;
    const next = textOf(payload);
    seeded.current = {
      version: payload.version,
      text: next,
      clickFloorEnabled: payload.clickFloorEnabled,
    };
    setText(next);
    setClickFloorEnabled(payload.clickFloorEnabled);
  }, [payload, untouched]);
  // Touched AND the row moved underneath: the fields are the operator's, the
  // diff and the footer are the new row's. Say so rather than letting the
  // submit label disagree with the numbers on screen.
  const movedUnderEdit = payload.version !== seeded.current.version;

  const parsed = Object.fromEntries(
    NUMERIC_KEYS.map((key) => [key, parseWhole(text[key])]),
  ) as Record<NumericKey, number | null>;

  // Per-field: a whole number inside its bounds.
  const errors: Partial<Record<NumericKey, string>> = {};
  for (const key of NUMERIC_KEYS) {
    const value = parsed[key];
    if (value === null) {
      errors[key] = "Must be a whole number.";
      continue;
    }
    const { min, max } = BOUNDS[key];
    if (value < min || value > max) {
      errors[key] = `Must be between ${min.toLocaleString()} and ${max.toLocaleString()}.`;
    }
  }
  // Cross-field, the backend's own rule: the backstop must cover at least one
  // full low-volume window past the allowance's own two, or it is not a
  // backstop. Judged on the values that WOULD result, so moving the window
  // alone can trip it.
  const nextWindow = parsed.lowVolumeWindowDays ?? payload.lowVolumeWindowDays;
  const nextBackstop =
    parsed.lowVolumeZeroBackstopDays ?? payload.lowVolumeZeroBackstopDays;
  if (!errors.lowVolumeZeroBackstopDays && nextBackstop < nextWindow * 2) {
    errors.lowVolumeZeroBackstopDays = `Must be at least twice the low-volume window — ${nextWindow * 2} days at a ${nextWindow}-day window — since the allowance already reads two windows.`;
  }

  // The body: changed fields only, plus the reason. The server refuses a
  // body that changes nothing, so an unchanged field is never sent.
  const body: UpdateTrackingSettingsBody = { reason: reason.trim() };
  for (const key of NUMERIC_KEYS) {
    const value = parsed[key];
    if (value !== null && value !== payload[key]) body[key] = value;
  }
  if (clickFloorEnabled !== payload.clickFloorEnabled) {
    body.clickFloorEnabled = clickFloorEnabled;
  }
  const changed = Object.keys(body).length - 1;
  const valid = Object.keys(errors).length === 0;
  const canSubmit = valid && changed > 0 && body.reason.length > 0;

  const tabNeedsAttention = (keys: NumericKey[]) =>
    keys.some((key) => errors[key]);

  const field = (key: NumericKey, widthClass = "!w-20") => (
    <input
      type="number"
      step={1}
      min={BOUNDS[key].min}
      max={BOUNDS[key].max}
      className={clsx(
        INPUT_CLASS,
        widthClass,
        "inline-block mx-1 py-1 text-center tabular-nums",
        errors[key] && "border-red-400",
      )}
      value={text[key]}
      onChange={(e) => setText({ ...text, [key]: e.target.value })}
    />
  );

  /** The bound line under a sentence: the default beside the range, so the
   *  operator sees where the fleet started without leaving the field. */
  const bounds = (key: NumericKey) =>
    `default ${payload.defaults[key]} · ${BOUNDS[key].min.toLocaleString()}–${BOUNDS[key].max.toLocaleString()}`;

  const fieldErrors = (keys: NumericKey[]) =>
    keys
      .filter((key) => errors[key])
      .map((key) => (
        <p key={key} className="text-xs text-red-600 mt-1">
          {FIELD_NAMES[key]}: {errors[key]}
        </p>
      ));

  return (
    <ActionModal
      title="Tracking Health settings"
      submitLabel={
        changed > 0
          ? `Save ${changed} change${changed === 1 ? "" : "s"}`
          : "Save changes"
      }
      onSubmit={() => onSubmit(body)}
      onClose={onClose}
      pending={pending}
      disabled={!canSubmit}
      wide
    >
      {/* Tabs. A tab holding an invalid field is marked so an error on the
          Advanced tab cannot silently hold the General tab's save hostage. */}
      <div className="flex gap-1 border-b border-[#E9E9EB]">
        {TABS.map(({ key, label }) => {
          const attention = tabNeedsAttention(
            key === "general" ? GENERAL_KEYS : ADVANCED_KEYS,
          );
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={clsx(
                "px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors",
                tab === key
                  ? "border-brand-primary text-content-primary"
                  : "border-transparent text-gray-500 hover:text-content-primary",
              )}
            >
              {label}
              {attention && (
                <span
                  className="ml-1 text-red-600"
                  data-tip="A value on this tab is outside its bounds"
                >
                  •
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "general" ? (
        <div>
          <label className={FIELD_LABEL_CLASS}>Low volume</label>
          <p className="text-sm text-content-primary leading-8">
            Low volume: fewer than {field("lowVolumeBookings")} qualifying
            bookings in each of the last two {field("lowVolumeWindowDays")}
            -day windows
          </p>
          <p className="text-xs text-gray-400">
            bookings — {bounds("lowVolumeBookings")} · window —{" "}
            {bounds("lowVolumeWindowDays")} days
          </p>
          {fieldErrors(GENERAL_KEYS)}
          {parsed.lowVolumeBookings === 0 && (
            <p className="text-xs text-amber-700 mt-1">
              0 switches the bucket off fleet-wide: nothing holds fewer than
              zero bookings, so no hotel is classified Low volume and the Rule
              1, 4 and 5 allowance goes with it.
            </p>
          )}
          <p className="text-xs text-gray-500 mt-3">
            A Healthy hotel under this line in BOTH windows reports Low volume:
            Rules 1, 4 and 5 stand down for it, nothing is locked, and the fleet
            screen lists it above Paused. Unhealthy and Monitoring hotels are
            never re-read this way — an open incident is a claim about the
            tracking, and how often the hotel books does not answer it. Two
            windows, not one: a hotel that booked eight last fortnight and none
            this fortnight is not low volume, it is dropping.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <label className={FIELD_LABEL_CLASS}>Rule 6 — the click floor</label>
            <label className="flex items-center gap-2 text-sm text-content-primary mb-1 cursor-pointer">
              <input
                type="checkbox"
                checked={clickFloorEnabled}
                onChange={(e) => setClickFloorEnabled(e.target.checked)}
              />
              Rule 6 is switched {clickFloorEnabled ? "on" : "off"}
              {clickFloorEnabled !== payload.clickFloorEnabled && (
                <span className="text-xs text-gray-400">
                  (was {payload.clickFloorEnabled ? "on" : "off"})
                </span>
              )}
            </label>
            <p
              className={clsx(
                "text-sm leading-8",
                clickFloorEnabled ? "text-content-primary" : "text-gray-400",
              )}
            >
              Rule 6: fewer than {field("clickFloorClicks", "!w-24")} Google Ads
              clicks in the trailing {field("clickFloorWindowDays")} days marks
              the hotel Unhealthy — go check the Google Ads account
            </p>
            <p className="text-xs text-gray-400">
              clicks — {bounds("clickFloorClicks")} · window —{" "}
              {bounds("clickFloorWindowDays")} days
              {!clickFloorEnabled &&
                " · switched off: every hotel's Rule 6 reads insufficient (rule-disabled) and nothing is judged"}
            </p>
            {fieldErrors(["clickFloorClicks", "clickFloorWindowDays"])}
            <p className="text-xs text-gray-500 mt-2">
              An Ads-side rule, deliberately: an account that is not delivering
              clicks is a Google Ads problem, not a tracking one, and the hotel
              is marked Unhealthy so a person goes and looks at the account. Not
              stood down by the low-volume allowance — clicks are the signal
              upstream of bookings.
            </p>
          </div>
          <div>
            <label className={FIELD_LABEL_CLASS}>
              The allowance's backstop
            </label>
            <p className="text-sm text-content-primary leading-8">
              A zero persisting {field("lowVolumeZeroBackstopDays")} days with
              matching click volume fails Rule 1 despite the allowance
            </p>
            <p className="text-xs text-gray-400">
              {bounds("lowVolumeZeroBackstopDays")} days · at least twice the
              low-volume window (currently ≥ {nextWindow * 2})
            </p>
            {fieldErrors(["lowVolumeZeroBackstopDays"])}
            <p className="text-xs text-gray-500 mt-2">
              The hole the two windows do not close: a hotel that is genuinely
              quiet all off-season and then breaks reads zero in every window
              from then on, so both stay under the threshold while clicks climb
              in high season. With zero qualifying bookings across this many
              days and click opportunity to match, Rule 1's failure stands. A
              quiet hotel is not a dead one.
            </p>
          </div>
        </div>
      )}

      {movedUnderEdit && (
        <p className="text-xs text-amber-700">
          These settings changed while you were editing —{" "}
          {fmtActor(payload.updatedBy) || "someone"} saved version{" "}
          {payload.version}. Your fields are untouched; what you save is
          computed against the NEW values, so re-check the diff before saving.
        </p>
      )}

      <div>
        <label className={FIELD_LABEL_CLASS}>Reason (required)</label>
        <textarea
          className={`${INPUT_CLASS} min-h-[66px]`}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why these numbers — the next operator reads this beside the change"
        />
      </div>

      <p className="text-xs text-gray-400">
        Takes effect on the next daily pass (04:30 ET), when the derived
        Low-volume bucket and Rule 6 are re-read — nothing moves the moment you
        save. Past evaluation records keep the values they were judged under.
        {changed === 0 && " Nothing has changed yet."}
      </p>

      <p className="text-xs text-gray-400 border-t border-[#E9E9EB] pt-2">
        {payload.version === 0
          ? "code defaults (nothing saved yet)"
          : `last changed by ${fmtActor(payload.updatedBy) || "unknown"} ${fmtDate(
              payload.updatedAt,
            )}${payload.updatedReason ? ` — ${payload.updatedReason}` : ""} · version ${
              payload.version
            }`}
      </p>
    </ActionModal>
  );
}

/** How each field is named in an error line — the sentence's own words, not
 *  the wire key. */
const FIELD_NAMES: Record<NumericKey, string> = {
  lowVolumeBookings: "Bookings",
  lowVolumeWindowDays: "Window",
  lowVolumeZeroBackstopDays: "Backstop",
  clickFloorClicks: "Clicks",
  clickFloorWindowDays: "Click window",
};
